/**
 * Reset Password Form Component
 * Allows users to set a new password using a reset token from the URL
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import './AuthForms.css';

function ResetPasswordForm() {
  const { resetPassword, error, clearError } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
  });

  // Apply saved theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'gold';
    document.body.setAttribute('data-theme', savedTheme);
  }, []);

  // Extract token from URL on mount
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setFormError('Invalid or missing reset token. Please request a new password reset.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  // Validate password as user types
  useEffect(() => {
    setPasswordValidation({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
    });
  }, [password]);

  const isPasswordValid = () => {
    return (
      passwordValidation.length &&
      passwordValidation.uppercase &&
      passwordValidation.lowercase &&
      passwordValidation.number
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    clearError();

    // Validate token exists
    if (!token) {
      setFormError('Invalid or missing reset token. Please request a new password reset.');
      return;
    }

    // Validate password
    if (!password) {
      setFormError('Please enter a new password');
      return;
    }

    if (!isPasswordValid()) {
      setFormError('Password does not meet requirements');
      return;
    }

    // Validate password confirmation
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setResetLoading(true);
    try {
      const result = await resetPassword(token, password);

      if (result.success) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setFormError(result.error || 'Failed to reset password');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  return (
    <div className="auth-form-container">
      <div className="auth-form-header">
        <div className="auth-logo">
          <span className="logo-icon">⛸️</span>
          <h1>Lumi</h1>
        </div>
        <p className="auth-subtitle">Your Figure Skating Training Assistant</p>
      </div>

      <form onSubmit={handleSubmit} className="auth-form">
        <h2>Set New Password</h2>

        {success ? (
          <div className="auth-success">
            <p>Your password has been reset successfully!</p>
            <p>Redirecting to login page...</p>
          </div>
        ) : (
          <>
            {!token ? (
              <div className="auth-error">
                Invalid or missing reset token. Please request a new password reset.
              </div>
            ) : (
              <>
                <p className="auth-description">
                  Enter your new password below. Make sure it meets the security requirements.
                </p>

                {(formError || error) && (
                  <div className="auth-error">
                    {formError || error}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="password">New Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    disabled={resetLoading}
                    autoComplete="new-password"
                  />
                </div>

                {/* Password Requirements */}
                <div className="password-requirements">
                  <p className="requirements-title">Password must contain:</p>
                  <ul>
                    <li className={passwordValidation.length ? 'valid' : ''}>
                      {passwordValidation.length ? '✓' : '○'} At least 8 characters
                    </li>
                    <li className={passwordValidation.uppercase ? 'valid' : ''}>
                      {passwordValidation.uppercase ? '✓' : '○'} One uppercase letter
                    </li>
                    <li className={passwordValidation.lowercase ? 'valid' : ''}>
                      {passwordValidation.lowercase ? '✓' : '○'} One lowercase letter
                    </li>
                    <li className={passwordValidation.number ? 'valid' : ''}>
                      {passwordValidation.number ? '✓' : '○'} One number
                    </li>
                  </ul>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    disabled={resetLoading}
                    autoComplete="new-password"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <span className="field-error">Passwords do not match</span>
                  )}
                </div>

                <button
                  type="submit"
                  className="auth-button primary"
                  disabled={resetLoading || !isPasswordValid() || password !== confirmPassword}
                >
                  {resetLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </>
            )}
          </>
        )}

        <div className="auth-divider">
          <span>Remember your password?</span>
        </div>

        <button
          type="button"
          className="auth-button secondary"
          onClick={handleBackToLogin}
        >
          Back to Sign In
        </button>
      </form>
    </div>
  );
}

export default ResetPasswordForm;
